import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Link as MLink
} from "@mui/material";
import Fade from "@mui/material/Fade";
import { useLocation, Link as RouterLink } from "react-router-dom";

/** גרסת המדיניות בפורמט YYYY-MM-DD.
 *  עדכן לתאריך העדכון בפועל, למשל "2025-10-15".
 */
const CONSENT_VERSION = "2026-09-26";
const STORAGE_KEY = "bidit.cookieConsent";
const CONSENT_TTL_DAYS = 365;

/** מציגים בדפים: בית + רישומי לקוח/פרו */
const VISIBLE_EXACT = new Set([
  "/",                 // דף הבית
  "/signupwithphone",  // רישום לקוח
  "/signupasprof",     // רישום בעל מקצוע
]);
// כיסוי עתידי לנתיבים שמתחילים ב-/signup או /register
const VISIBLE_PREFIXES = ["/signup", "/register"];

function normalizePath(p) {
  if (!p) return "/";
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}
function shouldShowPath(pathname) {
  const p = normalizePath(pathname);
  if (VISIBLE_EXACT.has(p)) return true;
  return VISIBLE_PREFIXES.some((prefix) => p.startsWith(prefix));
}

function hasValidConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { ts, v } = JSON.parse(raw);
    if (v !== CONSENT_VERSION) return false; // גרסה/תאריך השתנו - מציגים שוב
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < CONSENT_TTL_DAYS;
  } catch {
    return false;
  }
}

export default function CookieConsentBlocking() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = shouldShowPath(location.pathname) && !hasValidConsent();
    setOpen(show);
  }, [location.pathname]);

  const saveConsent = (marketing) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ts: Date.now(), v: CONSENT_VERSION, marketing: Boolean(marketing) })
      );
      window.dispatchEvent(new Event("bidit-cookie-consent"));
      if (marketing) window.dispatchEvent(new Event("bidit-marketing-consent"));
    } catch {}
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      dir="rtl"
      fullWidth
      maxWidth="xs"            // חלון קומפקטי
      scroll="body"
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      keepMounted
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 220 }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(2px)" } } }}
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: "linear-gradient(135deg, #ffffff 0%, #fdf8f3 100%)",
          fontFamily: "Assistant, sans-serif",
          p: { xs: 0.5, sm: 1 },
          boxShadow: "0 6px 25px rgba(0,0,0,0.15)",
          maxHeight: "none",
          width: { xs: "88%", sm: 420 },
          mx: "auto",
        },
      }}
      sx={{ zIndex: (t) => t.zIndex.modal + 20 }}
    >
      <DialogTitle
        id="cookie-consent-title"
        sx={{
          textAlign: "center",
          fontWeight: 700,
          fontSize: { xs: 18, sm: 20 },
          color: "#333",
          mb: 0,
          pt: { xs: 2, sm: 2.5 },
          fontFamily: "Assistant, sans-serif",
        }}
      >
        🍪 מדיניות עוגיות
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          py: { xs: 1.5, sm: 2.5 },
          textAlign: "center",
          backgroundColor: "transparent",
          overflow: "visible",
        }}
      >
        <Box sx={{ maxWidth: 520, mx: "auto", px: { xs: 1.5, sm: 2 } }}>
          <Typography
            id="cookie-consent-desc"
            variant="body1"
            sx={{
              color: "#444",
              fontSize: { xs: 15, sm: 16 },
              lineHeight: 1.8,
              fontFamily: "Assistant, sans-serif",
              mb: { xs: 1, sm: 1.5 },
            }}
          >
            אנחנו משתמשים בעוגיות חיוניות להפעלה תקינה ואבטחה. בהסכמתך, נשתמש גם בכלי מדידה ופרסום של Meta כדי למדוד ביצועי קמפיינים והרשמות.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontSize: { xs: 13.5, sm: 14.5 },
              fontFamily: "Assistant, sans-serif",
            }}
          >
            לפרטים מלאים ראה{" "}
            <MLink
              component={RouterLink}
              to="/cookie-policy"
              underline="hover"
              sx={{
                color: "#ff8000",
                fontWeight: 600,
                transition: "0.25s",
                "&:hover": { color: "#e56f00" },
              }}
            >
              מדיניות העוגיות
            </MLink>.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 1, pb: { xs: 1.5, sm: 2 }, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          onClick={() => saveConsent(false)}
          sx={{
            color: "#555",
            borderColor: "#bbb",
            fontWeight: 700,
            borderRadius: "30px",
            fontFamily: "Assistant, sans-serif",
            px: { xs: 2.2, sm: 3 },
          }}
        >
          חיוניות בלבד
        </Button>
        <Button
          variant="contained"
          onClick={() => saveConsent(true)}
          sx={{
            backgroundColor: "#ff8000",
            fontWeight: 700,
            borderRadius: "30px",
            fontFamily: "Assistant, sans-serif",
            px: { xs: 3, sm: 4 },
            py: { xs: 0.75, sm: 1 },
            fontSize: { xs: 14, sm: 16 },
            boxShadow: "0 3px 10px rgba(255,128,0,0.28)",
            "&:hover": {
              backgroundColor: "#e97400",
              boxShadow: "0 3px 12px rgba(255,128,0,0.4)",
            },
          }}
        >
          מאשר/ת מדידה ופרסום
        </Button>
      </DialogActions>
    </Dialog>
  );
}
